import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useLoginMutation } from '../../../pages/authentication/data-access/+state/user.api';
import { storeClientCredentials } from '../../../app/shared/utils/cookie.utils';
import { setAccountDomain } from '@app/shared/utils/accountDomain.utils';

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [validated, setValidated] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormErrors({});
    setValidated(true);

    const errors: { [key: string]: string } = {};

    if (!username) {
      errors.username = 'Email is required';
    } else if (!isValidEmail(username)) {
      errors.username = 'Please enter a valid email';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const data = await login({ username, password }).unwrap();

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('userId', data.userId);
      if (data.role != null && data.role !== '') {
        localStorage.setItem('role', String(data.role).trim());
      }
      if (data.organizationId) {
        localStorage.setItem('organizationId', data.organizationId);
      }
      if (data.credentials) {
        storeClientCredentials(data.credentials);
      }
      if (data.features && Array.isArray(data.features)) {
        localStorage.setItem('features', JSON.stringify(data.features));
      }
      // Add firstName to localStorage if available
      if (data.firstName) {
        localStorage.setItem('firstName', data.firstName);
      }
      // Also save username (email) for fallback
      if (username) {
        localStorage.setItem('username', username);
      }
      if (data.domain) {
        setAccountDomain(data.domain);
      }

      navigate('/product-selection');
    } catch (err: any) {
      const msg = err?.data?.message || 'Login failed. Please check your credentials.';
      setAlertMessage(msg);
      setShowAlert(true);
    }
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">Sign In</h3>
        <p className="text-body-tertiary">Get access to your account</p>
      </div>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Form.Group className="mb-3 text-start">
          <Form.Label>Email address</Form.Label>
          <InputGroup hasValidation>
            <InputGroup.Text id="inputGroupPrepend">@</InputGroup.Text>
            <Form.Control
              type="email"
              placeholder="Enter your email"
              required
              disabled={isLoading}
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                setFormErrors(prev => ({ ...prev, username: '' }));
              }}
              isInvalid={!!formErrors.username}
            />
            <Form.Control.Feedback type="invalid">{formErrors.username}</Form.Control.Feedback>
          </InputGroup>
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label>Password</Form.Label>
          <InputGroup hasValidation>
            <Form.Control
              type="password"
              placeholder="Enter your password"
              required
              disabled={isLoading}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                setFormErrors(prev => ({ ...prev, password: '' }));
              }}
              isInvalid={!!formErrors.password}
            />
            <Form.Control.Feedback type="invalid">{formErrors.password}</Form.Control.Feedback>
          </InputGroup>
        </Form.Group>

        <Row className="flex-between-center mb-7">
          <Col xs="auto">
            <Form.Check type="checkbox" className="mb-0">
              <Form.Check.Input
                type="checkbox"
                name="remember-me"
                id="remember-me"
                defaultChecked
                disabled={isLoading}
              />
              <Form.Check.Label htmlFor="remember-me" className="mb-0">
                Remember me
              </Form.Check.Label>
            </Form.Check>
          </Col>
          <Col xs="auto">
            <Link to={`/pages/authentication/${layout}/forgot-password`} className="fs-9 fw-semibold">
              Forgot Password?
            </Link>
          </Col>
        </Row>

        <Button type="submit" variant="primary" className="w-100 mb-3" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" className="me-2" />
              Logging in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
      </Form>

      <div className="text-center">
        <Link to="/sign-up" className="fs-9 fw-bold">
          Create an account
        </Link>
      </div>

      {/* MUI Snackbar for login failure */}
      <Snackbar
        open={showAlert}
        autoHideDuration={4000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert onClose={() => setShowAlert(false)} severity="error" variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default SignInForm;
